<template>
  <div
    class="AwsSearchView">

    <div
      class="AwsSearchView_BrandedHeader">
      <img
        class="_icon"
        src="@/assets/aws-data-exchange-icon.svg"
        alt="AWS Data Exchange Icon"
      />
      AWS Data Exchange
    </div>

    <SearchGalleryElastic
      class="AwsSearchView_SearchGalleryElastic"
      ref="searchGalleryElastic"
      :apps="apps"
      :is-loading="isLoading"
      :is-paginating="isPaginating"
      :is-searching="isSearching || isSorting"
      :filter-map="filterMap"
      :filters="filters"
      :search-text="searchText"
      :active-filters="activeFilters"
      :sort-options="sortOptions"
      :sort-value="sortValue"
      :results-count="resultsCount"
      :is-showing-all-results="isShowingAllResults"
      :is-logos-square="false"
      @click-result="handleResultClick"
      @sort="handleSort"
      @search="handleSearch"
      @filter="handleFilter"
      @paginate="handlePaginate"
    />

  </div>
</template>

<script>
import axios from 'axios';
import { isEmpty, debounce } from 'lodash';
import { AWS_FILTERS, AWS_SEARCH } from '@/queries';
import SearchGalleryElastic from '@/components/SearchGalleryElastic.vue';

const AWS_FILTER_MAP = [
  {
    key: 'CATEGORY',
    label: 'Category',
    searchBar: true,
    searchPlaceholder: 'Search categories',
    showCount: 5,
    priority: 3,
  },
  {
    key: 'VENDOR',
    label: 'Vendor',
    searchBar: true,
    searchPlaceholder: 'Search vendors',
    showCount: 5,
    priority: 2,
  },
  {
    key: 'PRICING_MODEL',
    label: 'Pricing Model',
    searchBar: false,
    searchPlaceholder: 'Search pricing models',
    showCount: 5,
    priority: 1,
  },
];

const AWS_SORT_OPTIONS = [
  {
    value: 'relevance',
    label: 'Relevance',
    order: 'asc',
  },
  {
    value: 'creation_time',
    label: 'Creation time',
    order: 'asc',
  },
  {
    value: 'modified_time',
    label: 'Modified time',
    order: 'asc',
  },
  {
    value: 'average_rating',
    label: 'Average rating',
    order: 'desc',
  },
];

export default {
  name: 'AwsSearchView',

  components: {
    SearchGalleryElastic,
  },

  metaInfo() {
    return {
      title: 'Data Integration Platform',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: 'Domo\'s Integration Platform has over a thousand integrations to the tools you use every day. Use our pre-built solutions or utilize our ecosystem to build your own.',
        },
      ],
    };
  },

  data() {
    return {
      isLoading: true,
      isPaginating: false,
      isSearching: false,
      isSorting: false,
      searchText: this.$route.query.q || '',
      apps: [],
      filters: [],
      activeFilters: [],
      filterMap: AWS_FILTER_MAP,
      sortOptions: AWS_SORT_OPTIONS,
      sortValue: AWS_SORT_OPTIONS[0],
      resultsCount: 0,
      isShowingAllResults: true,
      nextToken: null,
    };
  },

  async mounted() {
    try {
      const { data } = await axios.get(AWS_FILTERS);

      this.filters = data.filters;

      this.activeFilters = this.activeFiltersFromQueryParams(this.$route.query);

      this.$root.remoteDataFetched('AWS Filters', true, data);

      await this.fetchApps('AWS Search', 'isLoading');
    } catch (err) {
      this.$root.remoteDataFetched('AWS Filters', false, err);
    }
  },

  methods: {
    handleResultClick(app) {
      this.$analytics.anchanchu.trackEvent(
        'clicked app', {
          'app name': app.title,
          'awsdx flow': (this.$root.userIsCustomer) ? 'log in' : 'sign up',
        },
      );

      const computedPath = this.$root.userIsCustomer
        ? '/login'
        : '/start/appstore';

      const computedUrl = `https://www.domo.com${computedPath}?utmrefresh=1&utm_source=${app.title}&utm_medium=domo&utm_campaign=Appstore_Domo_${app.appstoreID}_awsdx&utm_campid=701f2000001C6UOAA0`;

      window.open(encodeURI(computedUrl), '_blank');
    },

    handleSort(sortValue) {
      this.sortValue = sortValue;
      this.fetchApps('AWS Sort', 'isSorting');
    },

    handleSearch: debounce(function handleSearchDebounced(searchText) {
      this.searchText = searchText;
      this.fetchApps('AWS Search', 'isSearching');
    }, 300),

    handleFilter(newActiveFilters) {
      this.activeFilters = newActiveFilters;
      this.fetchApps('AWS Filter', 'isSearching');
    },

    handlePaginate() {
      if (!this.nextToken) return;
      this.fetchApps('AWS Paginate', 'isPaginating');
    },

    async fetchApps(requestName, toggleValue = null) {
      if (toggleValue) this[toggleValue] = true;

      const SAME_ROUTE_VALUES = [
        'isLoading',
        'isSorting',
        'isPaginating',
      ];

      const isPagination = toggleValue === 'isPaginating';

      try {
        const { data } = await axios.post(AWS_SEARCH, {
          nextToken: isPagination ? this.nextToken : null,
          text: this.searchText || null,
          filters: this.postFiltersFromActiveFilters(),
          sortBy: this.sortValue.value || null,
        });

        this.apps = isPagination ? [...this.apps, ...data.apps] : data.apps;

        this.resultsCount = data.totalResultCount;
        this.nextToken = data.nextToken;

        this.$root.remoteDataFetched(requestName, true, data);
      } catch (err) {
        this.$root.remoteDataFetched(requestName, false, err);
      } finally {
        if (!isPagination) {
          this.$refs.searchGalleryElastic.scrollToTopOfResults();
        }

        if (!SAME_ROUTE_VALUES.includes(toggleValue)) {
          const filterQueryParams = this.activeFiltersToQueryParams();

          this.$router.replace({
            name: this.$route.name,
            query: this.searchText
              ? { q: this.searchText, ...filterQueryParams }
              : { ...filterQueryParams },
          });
        }

        this.isShowingAllResults = !this.searchText && !this.activeFilters.length;

        if (toggleValue) this[toggleValue] = false;
      }
    },

    activeFiltersToQueryParams() {
      const rawParams = {};

      this.filters.forEach((filter) => {
        rawParams[filter.key.toLowerCase()] = [...this.activeFilters]
          .filter((activeFilter) => activeFilter.groupKey === filter.key)
          .map((activeFilter) => activeFilter.label)
          .sort()
          .join(',');
      });

      const cleanedParams = {};

      Object.keys(rawParams).forEach((p) => {
        if (rawParams[p].length) {
          cleanedParams[p] = rawParams[p];
        }
      });

      return cleanedParams;
    },

    activeFiltersFromQueryParams(queryParams) {
      const activeFilters = [];

      Object.keys(queryParams).forEach((paramName) => {
        const paramNamedUpperCased = paramName.toUpperCase();
        const thisGroupFilters = this.filters.find((f) => f.key === paramNamedUpperCased);

        if (!thisGroupFilters) return;

        const requestedFilters = queryParams[paramName].split(',');

        const activatedFilters = thisGroupFilters.values
          .filter((f) => requestedFilters.includes(f.label))
          .map((f) => ({
            groupKey: paramNamedUpperCased,
            ...f,
          }));

        activeFilters.push(...activatedFilters);
      });

      return activeFilters;
    },

    postFiltersFromActiveFilters() {
      if (!this.activeFilters.length) return null;

      const valuesByKey = {};

      this.activeFilters.forEach((activeFilter) => {
        if (valuesByKey[activeFilter.groupKey] === undefined) {
          valuesByKey[activeFilter.groupKey] = [];
        }
        valuesByKey[activeFilter.groupKey].push(activeFilter.value);
      });

      if (isEmpty(valuesByKey)) return null;

      return Object.keys(valuesByKey).map((key) => ({
        type: key,
        values: [...valuesByKey[key]],
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

$branded-header-height: 64px;

.AwsSearchView {
  position: relative;
  height: calc(100vh - 60px);
}

.AwsSearchView_BrandedHeader {
  height: $branded-header-height;
  padding-left: 24px;
  color: $white;
  font-size: 20px;
  font-weight: 600;
  line-height: $branded-header-height;
  white-space: nowrap;
  background-image: url('~@/assets/aws-data-exchange-bg.png');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  ._icon {
    position: relative;
    top: 8px;
    margin-right: 8px;
  }
}

.AwsSearchView_SearchGalleryElastic {
  height: calc(100% - 64px);
}
</style>
